import { FormControlLabel, Grid, Radio, Typography } from "@material-ui/core";
import TextField from "@material-ui/core/TextField";
import { makeStyles } from "@material-ui/core/styles";
import * as React from "react";
import { FC, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";
import { CandidateViewModel } from "../../model/CandidateViewModel";
import { Switzerland } from "../../data/constants";
import Places from "../../data/placeOfOrigin.json";
import { isTranslationKey, TranslationKey } from "../../translations/TranslationKey";
import { GetTranslation } from "../../translations/GetTranslation";
import FormGridCell from "../common/FormGridCell";
import {
  CompanySelectField,
  CountrySelectField,
  DateInput,
  GenderSelectField,
  PlaceOfOriginSelectField,
  RadioInput,
  TextInput,
} from "../startCertificationWizard_new/components/shared/Fields";
import { getValidDate } from "../../utils/Dates";
import TranslatedMessage from "../translations/TranslatedMessage";
import Alert from "@material-ui/lab/Alert";
import { ObjectHelper } from "../../utils/ObjectHelper";

type LifeCandidateFieldsProps = {
  candidate: CandidateViewModel;
};

const useStyles = makeStyles((theme) => ({
  topSection: {
    paddingBottom: theme.spacing(2),
  },
  section: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(2),
  },
  infoAlert: {
    marginBottom: theme.spacing(2),
  },
}));

const LifeCandidateFields: FC<LifeCandidateFieldsProps> = ({ candidate }) => {
  const intl = useIntl();

  const { watch, setValue } = useFormContext();
  const [isAhvEditable] = React.useState(ObjectHelper.isEmpty(candidate.ahvNumber));

  const homeCountry = watch("homeCountry");
  const placeOfOrigin = watch("placeOfOrigin");
  const canton = watch("canton");
  const preferredContact = watch("preferredContact");

  const classes = useStyles();

  useEffect(() => {
    setValue("nationalityIsSwiss", homeCountry === Switzerland);
  }, [homeCountry, setValue]);

  useEffect(() => {
    const cantonObj = Places.find((place) => place.value === placeOfOrigin);
    setValue("canton", cantonObj?.canton || "");
  }, [placeOfOrigin, setValue]);

  React.useEffect(() => {
    setValue("privateIsPreferredContact", preferredContact === "private");
  }, [preferredContact, setValue]);

  const getCantonDisplayValue = () => {
    if (!canton) {
      return "";
    }
    const key = `canton.${canton.toLowerCase()}`;
    if (isTranslationKey(key)) {
      return GetTranslation(intl, key as TranslationKey);
    }
    return canton;
  };

  return (
    <>
      <Grid container={true} direction="column" spacing={8}>
        <Typography className={classes.topSection} variant="h6">
          {GetTranslation(intl, "person.section.personalDetails")}
        </Typography>
        <Alert severity="info" className={classes.infoAlert}>
          <TranslatedMessage id="lifeExams.messages.infoAlertAddUpdateOrConfirmPersonalData" />
        </Alert>
        <Grid container={true} spacing={4}>
          <FormGridCell size="third">
            <TextInput
              disabled={candidate?.disablePersonalFields}
              name="firstName"
              label={GetTranslation(intl, "person.field.firstName")}
              required
            />
          </FormGridCell>
          <FormGridCell size="third">
            <TextInput
              disabled={candidate?.disablePersonalFields}
              name="lastName"
              label={GetTranslation(intl, "person.field.lastName")}
              required
            />
          </FormGridCell>
          <FormGridCell size="third">
            <GenderSelectField name="gender" defaultValue={candidate?.gender} required />
          </FormGridCell>
          <Grid item={true} xs={12} sm={6} md={4} lg={4} xl={4} style={{ paddingTop: 0, paddingBottom: 0 }}>
            <DateInput
              disabled={candidate?.disablePersonalFields}
              name="birthdate"
              defaultValue={getValidDate(candidate.birthdate)}
              label={GetTranslation(intl, "person.field.birthDate")}
            />
          </Grid>
          <FormGridCell size="third">
            <TextInput
              disabled={candidate?.disablePersonalFields && !isAhvEditable}
              name="ahvNumber"
              label={GetTranslation(intl, "person.field.ahvNumber")}
              required
            />
          </FormGridCell>
          <FormGridCell size="third">
            <TextInput name="emailAddress" label={GetTranslation(intl, "person.field.email")} required />
          </FormGridCell>
          <FormGridCell size="third">
            <TextInput name="phoneNumber" label={GetTranslation(intl, "person.field.phoneNumber")} />
          </FormGridCell>
          <FormGridCell size="third">
            <TextInput name="mobileNumber" label={GetTranslation(intl, "person.field.mobileNumber")} />
          </FormGridCell>
          <FormGridCell size="third">
            <TextInput name="ciceroNumber" label={GetTranslation(intl, "person.field.ciceroNumber")} />
          </FormGridCell>
          <FormGridCell size="third">
            <CountrySelectField
              disabled={candidate?.disablePersonalFields}
              name="homeCountry"
              label={GetTranslation(intl, "person.field.homeCountry")}
              defaultValue={candidate.homeCountry}
              required
            />
          </FormGridCell>
          {homeCountry === Switzerland && (
            <>
              <FormGridCell size="third">
                <PlaceOfOriginSelectField
                  disabled={candidate?.disablePersonalFields}
                  name="placeOfOrigin"
                  defaultValue={candidate.placeOfOrigin}
                  required
                />
              </FormGridCell>
              <FormGridCell size="third">
                <TextField
                  required
                  fullWidth={true}
                  inputProps={{ readOnly: true }}
                  label={GetTranslation(intl, "candidate.field.canton")}
                  value={getCantonDisplayValue()}
                />
              </FormGridCell>
            </>
          )}
        </Grid>
        <Typography variant="h6" className={classes.section}>
          {GetTranslation(intl, "person.section.businessData")}
        </Typography>
        <Grid container={true} spacing={4}>
          <FormGridCell size="third">
            <CompanySelectField
              name="employerInformation"
              label={GetTranslation(intl, "person.field.employer")}
              defaultValue={candidate.employerInformation}
              required
            />
          </FormGridCell>
        </Grid>
        <Typography variant="h6" className={classes.section}>
          {GetTranslation(intl, "person.section.address")}
        </Typography>
        <Grid container={true} spacing={4}>
          <FormGridCell size="third">
            <TextInput name="street" label={GetTranslation(intl, "person.field.street")} required />
          </FormGridCell>
          <FormGridCell size="third">
            <TextInput
              name="streetNumber"
              label={GetTranslation(intl, "person.field.streetNumber")}
              required
              inputProps={{ maxLength: 9 }}
            />
          </FormGridCell>
          <FormGridCell size="third" />
          <FormGridCell size="third">
            <TextInput name="zipCode" label={GetTranslation(intl, "person.field.zipCode")} required />
          </FormGridCell>
          <FormGridCell size="third">
            <TextInput name="city" label={GetTranslation(intl, "person.field.city")} required />
          </FormGridCell>
          <FormGridCell size="third">
            <CountrySelectField
              name="country"
              label={GetTranslation(intl, "person.field.country")}
              defaultValue={candidate.country}
              required
            />
          </FormGridCell>
          <FormGridCell size="third">
            <RadioInput
              name="correspondenceLanguage"
              label={GetTranslation(intl, "candidate.field.correspondenceLanguage")}
              defaultValue={candidate.correspondenceLanguage}
              required
            >
              <FormControlLabel value="DE" control={<Radio />} label={GetTranslation(intl, "common.language.de")} />
              <FormControlLabel value="FR" control={<Radio />} label={GetTranslation(intl, "common.language.fr")} />
              <FormControlLabel value="IT" control={<Radio />} label={GetTranslation(intl, "common.language.it")} />
            </RadioInput>
          </FormGridCell>
          <FormGridCell size="third">
            <RadioInput
              name="preferredContact"
              label={GetTranslation(intl, "candidate.field.invoiceRecipient")}
              defaultValue={candidate.privateIsPreferredContact ? "private" : "business"}
            >
              <FormControlLabel value="private" control={<Radio />} label={GetTranslation(intl, "common.private")} />
              <FormControlLabel value="business" control={<Radio />} label={GetTranslation(intl, "common.business")} />
            </RadioInput>
          </FormGridCell>
        </Grid>
        {preferredContact === "business" && (
          <>
            <Typography className={classes.section} variant="h6">
              {GetTranslation(intl, "person.section.businessData")}
            </Typography>
            <Grid container={true} spacing={4}>
              <FormGridCell size="third">
                <TextInput name="employer" label={GetTranslation(intl, "person.field.employer")} required />
              </FormGridCell>
              <FormGridCell size="third">
                <TextInput name="employerStreet" label={GetTranslation(intl, "person.field.employerStreet")} required />
              </FormGridCell>
              <FormGridCell size="third">
                <TextInput
                  name="employerStreetNumber"
                  label={GetTranslation(intl, "person.field.employerStreetNumber")}
                  required
                  inputProps={{ maxLength: 9 }}
                />
              </FormGridCell>
              <FormGridCell size="third">
                <TextInput
                  name="employerZipCode"
                  label={GetTranslation(intl, "person.field.employerZipCode")}
                  required
                />
              </FormGridCell>
              <FormGridCell size="third">
                <TextInput name="employerCity" label={GetTranslation(intl, "person.field.employerCity")} required />
              </FormGridCell>
              <FormGridCell size="third">
                <CountrySelectField
                  name="employerCountry"
                  label={GetTranslation(intl, "person.field.country")}
                  defaultValue={candidate.country}
                  required
                />
              </FormGridCell>
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
};

export default LifeCandidateFields;
